export const MEETING_MESSAGES_ACTIVE_THREAD_TYPE = "wisefriend:active-message-thread"

const MEETING_MESSAGES_ACTIVE_THREAD_PREFIX = "wisefriend.meetingMessagesActiveThread"
const MEETING_MESSAGES_OPEN_COMMAND_CHANNEL = "wisefriend.meetingMessages"
const MEETING_MESSAGES_ACTIVE_THREAD_TTL_MS = 10 * 60 * 1000

export type MeetingMessagesActiveThread = {
  type: typeof MEETING_MESSAGES_ACTIVE_THREAD_TYPE
  meetingId: string
  panelToken: string
  threadId?: string | null
  recipientIdentityKey?: string | null
  active: boolean
  updatedAt: number
}

const getActiveThreadStorageKey = (meetingId: string, panelToken: string) =>
  `${MEETING_MESSAGES_ACTIVE_THREAD_PREFIX}.${meetingId}.${panelToken}`

const parseActiveThread = (
  value: unknown,
  meetingId: string,
  panelToken: string,
): MeetingMessagesActiveThread | null => {
  const payload = typeof value === "string" ? JSON.parse(value) : value
  if (!payload || typeof payload !== "object") return null
  const activeThread = payload as Partial<MeetingMessagesActiveThread>
  if (activeThread.type !== MEETING_MESSAGES_ACTIVE_THREAD_TYPE) return null
  if (activeThread.meetingId !== meetingId || activeThread.panelToken !== panelToken) return null
  if (!activeThread.updatedAt || Date.now() - activeThread.updatedAt > MEETING_MESSAGES_ACTIVE_THREAD_TTL_MS) {
    return null
  }
  return {
    type: MEETING_MESSAGES_ACTIVE_THREAD_TYPE,
    meetingId,
    panelToken,
    threadId: activeThread.threadId || null,
    recipientIdentityKey: activeThread.recipientIdentityKey || null,
    active: Boolean(activeThread.active && (activeThread.threadId || activeThread.recipientIdentityKey)),
    updatedAt: activeThread.updatedAt,
  }
}

const broadcastMeetingMessagesPayload = (payload: MeetingMessagesActiveThread) => {
  try {
    const Channel = window.BroadcastChannel
    if (typeof Channel === "function") {
      const channel = new Channel(MEETING_MESSAGES_OPEN_COMMAND_CHANNEL)
      channel.postMessage(payload)
      channel.close()
    }
  } catch {
    // Storage-backed handoff remains the fallback.
  }
}

export const writeMeetingMessagesActiveThread = (
  meetingId: string,
  panelToken: string,
  thread?: { id: string; recipientIdentityKey: string } | null,
) => {
  if (typeof window === "undefined") return
  const activeThread: MeetingMessagesActiveThread = {
    type: MEETING_MESSAGES_ACTIVE_THREAD_TYPE,
    meetingId,
    panelToken,
    threadId: thread?.id || null,
    recipientIdentityKey: thread?.recipientIdentityKey || null,
    active: Boolean(thread?.id || thread?.recipientIdentityKey),
    updatedAt: Date.now(),
  }
  try {
    window.localStorage.setItem(getActiveThreadStorageKey(meetingId, panelToken), JSON.stringify(activeThread))
  } catch {
    // The parent falls back to normal notification behavior if active-thread state cannot be stored.
  }
  broadcastMeetingMessagesPayload(activeThread)
}

export const readMeetingMessagesActiveThread = (
  meetingId: string,
  panelToken: string,
): MeetingMessagesActiveThread | null => {
  if (typeof window === "undefined") return null
  try {
    const raw = window.localStorage.getItem(getActiveThreadStorageKey(meetingId, panelToken))
    if (!raw) return null
    const activeThread = parseActiveThread(raw, meetingId, panelToken)
    return activeThread?.active ? activeThread : null
  } catch {
    return null
  }
}

export const subscribeToMeetingMessagesActiveThread = (
  meetingId: string,
  panelToken: string,
  onActiveThread: (activeThread: MeetingMessagesActiveThread | null) => void,
) => {
  if (typeof window === "undefined") return () => {}

  let channel: BroadcastChannel | null = null
  const handlePayload = (payload: unknown) => {
    try {
      const activeThread = parseActiveThread(payload, meetingId, panelToken)
      onActiveThread(activeThread?.active ? activeThread : null)
    } catch {
      // Ignore malformed cross-window messages.
    }
  }
  const handleStorage = (event: StorageEvent) => {
    if (event.key !== getActiveThreadStorageKey(meetingId, panelToken)) return
    if (!event.newValue) {
      onActiveThread(null)
      return
    }
    handlePayload(event.newValue)
  }

  onActiveThread(readMeetingMessagesActiveThread(meetingId, panelToken))
  try {
    const Channel = window.BroadcastChannel
    if (typeof Channel === "function") {
      channel = new Channel(MEETING_MESSAGES_OPEN_COMMAND_CHANNEL)
      channel.onmessage = (event) => handlePayload(event.data)
    }
  } catch {
    channel = null
  }
  window.addEventListener("storage", handleStorage)

  return () => {
    window.removeEventListener("storage", handleStorage)
    if (channel) {
      channel.close()
    }
  }
}
